import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import useProfile from '../hooks/useProfile';
import './profile-page.css';
import '../components/Onboarding/style.css';
import NavBar from '../components/NavBar/NavBar';
import LoadingAnimation from '../components/LoadingAnimation/LoadingAnimation';
import { BackIcon } from '../assets/icons/icons';
import { Link } from 'react-router-dom';
import {
  createCoachAccount,
  getMyCoachAccount,
  joinCoachByCode,
  leaveCoachMembership,
  listMyCoachMemberships,
} from '../api/coach';

export default function ProfilePage() {
  const { data, loading, error, updateBasics } = useProfile({ refreshOnMount: true });
  const profile = useMemo(() => data?.profile ?? null, [data]);
  const [isMobile, setIsMobile] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  const [form, setForm] = useState({
    name: '',
    age: '',
    gender: '',
    height_feet: '',
    height_inches: '',
    weight_lbs: '',
  });

  const [email, setEmail] = useState('');

  const [saving, setSaving] = useState(false);
  const [editBasics, setEditBasics] = useState(false);

  // Password update state
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const showInitialLoader = loading && !data;
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  // Coaching state
  const [coachAccount, setCoachAccount] = useState(null);
  const [coachDisplayName, setCoachDisplayName] = useState('');
  const [coachInviteCode, setCoachInviteCode] = useState('');
  const [coachMemberships, setCoachMemberships] = useState([]);
  const [joinCode, setJoinCode] = useState('');
  const [coachBusy, setCoachBusy] = useState(false);
  const [coachMsg, setCoachMsg] = useState('');
  const isCoachAccount = Boolean(coachAccount?.coach_id);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name ?? '',
        age: profile.age ?? '',
        gender: profile.gender ?? '',
        height_feet: profile.height_feet ?? '',
        height_inches: profile.height_inches ?? '',
        weight_lbs: profile.weight_lbs ?? '',
      });
      setEmail(profile.email ?? '');
    }
  }, [profile]);

  const refreshCoaching = async () => {
    try {
      const [{ data: acct }, { data: memberships }] = await Promise.all([
        getMyCoachAccount(),
        listMyCoachMemberships(),
      ]);
      setCoachAccount(acct || null);
      setCoachDisplayName(acct?.display_name || '');
      setCoachMemberships(memberships || []);
    } catch (_) {
      // Coaching tables/RPCs may not be deployed yet; ignore quietly.
    }
  };

  useEffect(() => {
    refreshCoaching();
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 798);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    setTimeout(() => setAnimateIn(true), 50);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    setPwMsg('');
    if (!pw1 || pw1.length < 8) { setPwMsg('Use at least 8 characters.'); return; }
    if (pw1 !== pw2) { setPwMsg('Passwords do not match.'); return; }
    try {
      const { error } = await supabase.auth.updateUser({ password: pw1 });
      if (error) throw error;
      setPwMsg('Password updated');
      setPw1(''); setPw2('');
    } catch (_) {
      setPwMsg('Failed to update password');
    }
  };

  const openFeedbackModal = () => {
    setFeedbackOpen(true);
    setFeedbackError('');
    setFeedbackSuccess('');
  };

  const closeFeedbackModal = () => {
    if (feedbackSending) return;
    setFeedbackOpen(false);
    setFeedbackMessage('');
    setFeedbackError('');
    setFeedbackSuccess('');
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    const message = feedbackMessage.trim();
    if (!message) {
      setFeedbackError('Please add a quick note before sending.');
      return;
    }
    setFeedbackSending(true);
    setFeedbackError('');
    setFeedbackSuccess('');
    try {
      let userId = profile?.user_id;
      if (!userId) {
        const { data: auth } = await supabase.auth.getUser();
        userId = auth?.user?.id;
      }
      if (!userId) throw new Error('Not authenticated');
      const { error: fbErr } = await supabase
        .from('platform_feedback')
        .insert([{ user_id: userId, source: 'profile', message }]);
      if (fbErr) throw fbErr;
      setFeedbackSuccess('Thanks for the feedback.');
      setFeedbackMessage('');
    } catch (err) {
      setFeedbackError(err?.message || 'Unable to send feedback.');
    } finally {
      setFeedbackSending(false);
    }
  };

  const onSaveBasics = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        height_feet: form.height_feet ? Number(form.height_feet) : null,
        height_inches: form.height_inches ? Number(form.height_inches) : null,
        weight_lbs: form.weight_lbs ? Number(form.weight_lbs) : null,
      };
      const res = await updateBasics(payload);
      if (res?.error) {
        alert('Failed to update profile.');
      } else {
        setEditBasics(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelBasics = (e) => {
    e.preventDefault();
    if (profile) {
      setForm({
        name: profile.name ?? '',
        age: profile.age ?? '',
        gender: profile.gender ?? '',
        height_feet: profile.height_feet ?? '',
        height_inches: profile.height_inches ?? '',
        weight_lbs: profile.weight_lbs ?? '',
      });
    }
    setEditBasics(false);
  };

  const onCreateOrRotateInvite = async () => {
    setCoachBusy(true);
    setCoachMsg('');
    setCoachInviteCode('');
    try {
      const { data, error: cErr } = await createCoachAccount({ displayName: coachDisplayName });
      if (cErr) throw cErr;
      setCoachInviteCode(data?.invite_code || '');
      setCoachMsg('Invite code generated');
      await refreshCoaching();
    } catch (e) {
      setCoachMsg(`Unable to generate invite code right now. ${e?.message ? `(${e.message})` : ''}`);
    } finally {
      setCoachBusy(false);
    }
  };

  const onJoinCoach = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setCoachBusy(true);
    setCoachMsg('');
    try {
      const { error: jErr } = await joinCoachByCode(joinCode.trim());
      if (jErr) throw jErr;
      setJoinCode('');
      setCoachMsg('Coach linked');
      await refreshCoaching();
    } catch (e) {
      setCoachMsg(`Invalid invite code or unable to link. ${e?.message ? `(${e.message})` : ''}`);
    } finally {
      setCoachBusy(false);
    }
  };

  const onLeaveCoach = async (membershipId) => {
    setCoachBusy(true);
    setCoachMsg('');
    try {
      const { error: dErr } = await leaveCoachMembership(membershipId);
      if (dErr) throw dErr;
      setCoachMsg('Coach unlinked');
      await refreshCoaching();
    } catch (_) {
      setCoachMsg('Unable to unlink right now.');
    } finally {
      setCoachBusy(false);
    }
  };

  function goBack() {
    if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  }

  if (error) return (
    <>
      <NavBar />
      <main className="web-view">
        <div className="container">
          <div className="pf-page">
            <div className="pf-page-header">
              <button className="pf-back" onClick={goBack} aria-label="Back"><BackIcon className="icon" /></button>
              <h1 className="pf-page-title">My Profile</h1>
            </div>
            <p>Error loading profile.</p>
          </div>
        </div>
      </main>
    </>
  );



  return (
    <>
      {showInitialLoader && <LoadingAnimation fullScreen />}
      <NavBar />
      <main className="web-view">
        <div className="container">
          <div className="pf-page">
            <div className="pf-page-header">
              <button className="pf-back" onClick={goBack} aria-label="Back"><BackIcon className="icon" /></button>
              <h1 className="pf-page-title">My Profile</h1>
            </div>
            <div className="pf-stack">
              {/* Basics Card */}
              <div className="pf-card">
                <div className="pf-card-header pf-card-actions-row">
                  <h3 className="pf-card-title">Basics</h3>
                  {!editBasics ? (
                    <button type="button" className="pf-card-action-btn" onClick={() => setEditBasics(true)}>Edit</button>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" className="pf-card-action-btn secondary" onClick={cancelBasics}>Cancel</button>
                      <button type="submit" form="pf-basics-form" className="pf-card-action-btn">Save</button>
                    </div>
                  )}
                </div>
                <div className="pf-card-body">
                  <form id="pf-basics-form" className="profile-form" onSubmit={onSaveBasics}>
                    <div className="pf-row">
                      <label className="pf-label">Email</label>
                      <input className="pf-input" type="email" value={email} readOnly disabled />
                    </div>
                    <div className="pf-row">
                      <label className="pf-label">Name</label>
                      <input className="pf-input" name="name" value={form.name} readOnly disabled placeholder="Full name" />
                    </div>
                    <div className="pf-row">
                      <label className="pf-label">Age</label>
                      <input className="pf-input" name="age" value={form.age} onChange={onChange} inputMode="numeric" disabled={!editBasics} />
                    </div>
                    <div className="pf-row">
                      <label className="pf-label">Gender</label>
                      <input className="pf-input" name="gender" value={form.gender} onChange={onChange} placeholder="e.g., Male/Female/Other" disabled={!editBasics} />
                    </div>
                    <div className="pf-row">
                      <label className="pf-label">Height (ft/in)</label>
                      <div className="pf-inline">
                        <input className="pf-input" name="height_feet" value={form.height_feet} onChange={onChange} inputMode="numeric" placeholder="ft" disabled={!editBasics} />
                        <input className="pf-input" name="height_inches" value={form.height_inches} onChange={onChange} inputMode="numeric" placeholder="in" disabled={!editBasics} />
                      </div>
                    </div>
                    <div className="pf-row">
                      <label className="pf-label">Weight (lbs)</label>
                      <input className="pf-input" name="weight_lbs" value={form.weight_lbs} onChange={onChange} inputMode="numeric" disabled={!editBasics} />
                    </div>
                    {/* Save handled by header action button when in edit mode */}
                  </form>
                </div>
              </div>

              {/* Password Card */}
              <div className="pf-card">
                <div className="pf-card-header">
                  <h3 className="pf-card-title">Update Password</h3>
                </div>
                <div className="pf-card-body">
                  <form onSubmit={updatePassword} className="profile-form">
                    <div className="pf-row">
                      <label className="pf-label">New Password</label>
                      <input className="pf-input" type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} placeholder="Enter new password" />
                    </div>
                    <div className="pf-row">
                      <label className="pf-label">Confirm Password</label>
                      <input className="pf-input" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password" />
                    </div>
                    <div className="pf-card-actions">
                      <button className="cta-btn pf-cta" type="submit">Update Password</button>
                      {pwMsg && <span className="pf-status">{pwMsg}</span>}
                    </div>
                  </form>
                </div>
              </div>

              {/* Gym/Membership Card */}
              <div className="pf-card">
                <div className="pf-card-header">
                  <h3 className="pf-card-title">Manage Subscription</h3>
                </div>
                <div className="pf-card-body">
                  <p className="pf-card-desc">Please email cole@craftedphysiques.com for support.</p>
                </div>
              </div>

              <div className="pf-card">
                <div className="pf-card-header pf-card-actions-row">
                  <h3 className="pf-card-title">Platform Feedback</h3>
                  <button type="button" className="pf-card-action-btn" onClick={openFeedbackModal}>Feedback?</button>
                </div>
                <div className="pf-card-body">
                  <p className="pf-card-desc">Share bugs, ideas, or anything we can improve.</p>
                </div>
              </div>

              {/* Coaching Card */}
              {isCoachAccount && (
                <div className="pf-card">
                  <div className="pf-card-header pf-card-actions-row">
                    <h3 className="pf-card-title">Coaching</h3>
                    {coachAccount?.coach_id && (
                      <Link className="pf-card-action-btn" to="/coach">Open Dashboard</Link>
                    )}
                  </div>
                  <div className="pf-card-body">
                    <div className="profile-form">
                      <div className="pf-row">
                        <label className="pf-label">Coach account</label>
                        <div className="pf-inline" style={{ gap: 10 }}>
                          <input
                            className="pf-input"
                            value={coachDisplayName}
                            onChange={(e) => setCoachDisplayName(e.target.value)}
                            placeholder="Coach display name (optional)"
                          />
                          <button
                            type="button"
                            className="cta-btn pf-cta"
                            style={{ width: 'auto', padding: '10px 14px' }}
                            onClick={onCreateOrRotateInvite}
                            disabled={coachBusy}
                          >
                            {coachAccount ? 'New Invite Code' : 'Create Coach Account'}
                          </button>
                        </div>
                        {coachAccount?.invite_code_last4 && (
                          <div className="pf-status">Current invite ends with {coachAccount.invite_code_last4}.</div>
                        )}
                        {coachInviteCode && (
                          <div className="pf-status">Invite code: <span style={{ fontFamily: 'monospace' }}>{coachInviteCode}</span></div>
                        )}
                      </div>

                      <div className="pf-row" style={{ marginTop: 8 }}>
                        <label className="pf-label">Link to a coach</label>
                        <form onSubmit={onJoinCoach} className="pf-inline" style={{ gap: 10 }}>
                          <input
                            className="pf-input"
                            value={joinCode}
                            onChange={(e) => setJoinCode(e.target.value)}
                            placeholder="Enter invite code"
                          />
                          <button className="cta-btn pf-cta" style={{ width: 'auto', padding: '10px 14px' }} type="submit" disabled={coachBusy}>
                            Join
                          </button>
                        </form>
                      </div>

                      {coachMemberships?.length > 0 && (
                        <div className="pf-row">
                          <label className="pf-label">Linked coaches</label>
                          <div style={{ display: 'grid', gap: 8 }}>
                            {coachMemberships.map((m) => (
                              <div key={m.id} className="pf-inline" style={{ justifyContent: 'space-between', gap: 10 }}>
                                <div className="pf-status" style={{ margin: 0 }}>
                                  Coach:{' '}
                                  {m.coach_display_name
                                    ? <span>{m.coach_display_name}</span>
                                    : <span style={{ fontFamily: 'monospace' }}>{m.coach_id}</span>}
                                </div>
                                <button type="button" className="pf-card-action-btn secondary" onClick={() => onLeaveCoach(m.id)} disabled={coachBusy}>
                                  Leave
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {coachMsg && <div className="pf-status" style={{ marginTop: 8 }}>{coachMsg}</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {feedbackOpen && (
        <div className="pf-feedback-overlay" onClick={closeFeedbackModal}>
          <div
            className="pf-feedback-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pf-feedback-header">
              <h3 className="pf-feedback-title">Platform feedback</h3>
              <button
                type="button"
                className="pf-feedback-close"
                onClick={closeFeedbackModal}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <p className="pf-feedback-copy">Tell us what is working or what we should fix.</p>
            <form className="pf-feedback-form" onSubmit={submitFeedback}>
              <textarea
                className="pf-input pf-feedback-textarea"
                rows="5"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                placeholder="Share your feedback..."
              />
              <div className="pf-feedback-actions">
                <button
                  type="button"
                  className="pf-card-action-btn secondary"
                  onClick={closeFeedbackModal}
                  disabled={feedbackSending}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pf-card-action-btn"
                  disabled={feedbackSending}
                >
                  {feedbackSending ? 'Sending...' : 'Send feedback'}
                </button>
              </div>
              {feedbackError && <div className="pf-feedback-status error">{feedbackError}</div>}
              {feedbackSuccess && <div className="pf-feedback-status success">{feedbackSuccess}</div>}
            </form>
          </div>
        </div>
      )}
    </>
  );
}

